import { AxiosRequestConfig } from 'axios';
import { get } from 'lodash';
import { useMutation, useQueryClient, UseMutationOptions } from 'react-query';
import { useInvalidateStores } from './useInvalidateStores';
import { ModelKeys } from './createKeys';

export interface MutationOptions<ModelType>
  extends UseMutationOptions<ModelType, unknown, AxiosRequestConfig, unknown> {
  onMutate?: (variables: object) => Promise<unknown>;
  onSuccess?: (
    data: ModelType,
    variables: object,
    context?: unknown,
  ) => Promise<unknown> | void;
  onError?: (
    err: Error,
    variables: object,
    context?: unknown,
  ) => Promise<unknown> | void;
  retry?: boolean | number | ((failureCount: number, error: Error) => boolean);
  meta?: Record<string, unknown>;
}

export const useCreateMutation = <ModelType, TParams extends object = {}>(
  key: readonly unknown[],
  fetch: (params: TParams) => Promise<ModelType>,
  fetchKey: ModelKeys['fetchKey'] = '',
  options: MutationOptions<ModelType> | undefined = undefined,
) => {
  const queryClient = useQueryClient();
  const invalidateStore = useInvalidateStores();
  const mutation = useMutation<ModelType, Error, TParams, unknown>(
    (params: TParams) => fetch(params),
    {
      ...options,
      onSuccess: (responseResource, params, context) => {
        if (options && options.onSuccess) {
          options.onSuccess(responseResource, params, context);
        }
        // ✅ invalidate the entity queries
        const entityId = get(responseResource, '_id', '');
        if (entityId) {
          queryClient.invalidateQueries([...key, 'entity', entityId]);
          invalidateStore(entityId, fetchKey);
        }
        // ✅ invalidate all the manys for this entity
        queryClient.invalidateQueries([...key, 'many']);
      },
    },
  );

  return {
    data: mutation.data,
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isLoading: mutation.isLoading,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
  };
};
